import { useState, useEffect } from "react";
import axios from "axios";
import { BarChart3, Clock, Mic, Image, FileText, Volume2, Activity, ChevronRight, Zap, Loader2, RefreshCw } from "lucide-react";
import { useNavigate } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProductionDashboard = ({ seriesFilter = "the-red-ink-chronicles" }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [genProgress, setGenProgress] = useState({ done: 0, total: 0, current: "" });
  const navigate = useNavigate();

  const fetchStats = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/production/dashboard-stats?series=${seriesFilter}`);
      setStats(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchStats(); }, [seriesFilter]);

  const generateNext = async (count = 5) => {
    if (!stats || stats.priority_queue.length === 0) return;
    setGenerating(true);
    const batch = stats.priority_queue.slice(0, count);
    setGenProgress({ done: 0, total: batch.length, current: "" });
    for (let i = 0; i < batch.length; i++) {
      const ep = batch[i];
      setGenProgress({ done: i, total: batch.length, current: `S${ep.season}E${ep.episode_number}: ${ep.title}` });
      try {
        await axios.post(`${API}/episodes/${ep.id}/tts`);
        // Poll until ready (max 120s)
        for (let j = 0; j < 60; j++) {
          await new Promise(r => setTimeout(r, 2000));
          const statusRes = await axios.get(`${API}/episodes/${ep.id}/tts-status`);
          if (statusRes.data.status === "ready" || statusRes.data.status === "error") break;
        }
      } catch (e) { console.error(`TTS failed for ${ep.title}`, e); }
    }
    setGenProgress({ done: batch.length, total: batch.length, current: "Complete!" });
    setGenerating(false);
    fetchStats();
  };

  if (loading) {
    return (
      <div className="py-16 flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!stats) return <p className="text-center text-gray-500 py-8">Failed to load dashboard data</p>;

  const { completion_pct, status_breakdown, tts_by_season, priority_queue } = stats;

  const STATUS_COLORS = {
    script: "bg-gray-600", assets: "bg-yellow-600", recording: "bg-blue-600",
    mixing: "bg-purple-600", complete: "bg-green-600"
  };

  const formatTimeAgo = (isoDate) => {
    if (!isoDate) return "Never";
    const diff = (Date.now() - new Date(isoDate).getTime()) / 1000;
    if (diff < 60) return "Just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  return (
    <div className="space-y-8" data-testid="production-dashboard">
      {/* Key Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: "Total Episodes", value: stats.total_episodes, icon: FileText, color: "text-white", bg: "bg-white/5" },
          { label: "Stories Written", value: stats.expanded, sub: `${completion_pct.stories}%`, icon: FileText, color: "text-green-400", bg: "bg-green-500/10 border border-green-500/20" },
          { label: "Thumbnails", value: stats.with_thumbnails, sub: `${completion_pct.thumbnails}%`, icon: Image, color: "text-purple-400", bg: "bg-purple-500/10 border border-purple-500/20" },
          { label: "TTS Audio", value: stats.with_tts_audio, sub: `${completion_pct.audio}%`, icon: Volume2, color: "text-blue-400", bg: "bg-blue-500/10 border border-blue-500/20" },
        ].map(m => (
          <div key={m.label} className={`p-5 rounded-xl ${m.bg}`} data-testid={`metric-${m.label.toLowerCase().replace(/\s/g, '-')}`}>
            <div className="flex items-center gap-2 mb-2">
              <m.icon size={16} className={m.color} />
              <span className="text-xs text-gray-500 uppercase tracking-wider">{m.label}</span>
            </div>
            <p className={`text-3xl font-bold font-mono ${m.color}`}>{m.value}</p>
            {m.sub && <p className="text-xs text-gray-500 mt-1">{m.sub} complete</p>}
          </div>
        ))}
      </div>

      {/* Audio Stats & Overall Progress */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Overall Completion */}
        <div className="p-6 border border-white/10 rounded-xl bg-white/[0.02]" data-testid="overall-completion">
          <h3 className="font-anton text-sm tracking-wider mb-4 flex items-center gap-2">
            <Activity size={16} className="text-red-500" /> OVERALL COMPLETION
          </h3>
          <div className="relative w-40 h-40 mx-auto mb-4">
            <svg viewBox="0 0 120 120" className="w-full h-full -rotate-90">
              <circle cx="60" cy="60" r="50" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="10" />
              <circle cx="60" cy="60" r="50" fill="none" stroke="#ef4444" strokeWidth="10"
                strokeDasharray={`${completion_pct.overall * 3.14} ${314 - completion_pct.overall * 3.14}`}
                strokeLinecap="round" className="transition-all duration-1000" />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-3xl font-bold font-mono">{completion_pct.overall}%</span>
            </div>
          </div>
          <div className="space-y-3">
            {[
              { label: "Stories", pct: completion_pct.stories, color: "bg-green-500" },
              { label: "Thumbnails", pct: completion_pct.thumbnails, color: "bg-purple-500" },
              { label: "Audio", pct: completion_pct.audio, color: "bg-blue-500" },
            ].map(b => (
              <div key={b.label} className="flex items-center gap-3">
                <span className="text-xs text-gray-400 w-20">{b.label}</span>
                <div className="flex-1 h-2 bg-white/10 rounded-full overflow-hidden">
                  <div className={`h-full ${b.color} rounded-full transition-all duration-700`} style={{ width: `${b.pct}%` }} />
                </div>
                <span className="text-xs font-mono text-gray-400 w-12 text-right">{b.pct}%</span>
              </div>
            ))}
          </div>
        </div>

        {/* Audio Production */}
        <div className="p-6 border border-white/10 rounded-xl bg-white/[0.02]" data-testid="audio-stats">
          <h3 className="font-anton text-sm tracking-wider mb-4 flex items-center gap-2">
            <Mic size={16} className="text-blue-500" /> AUDIO PRODUCTION
          </h3>
          <div className="space-y-4">
            <div className="flex items-center gap-4 p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
              <Clock size={28} className="text-blue-400" />
              <div>
                <p className="text-2xl font-bold font-mono text-blue-400">{stats.est_audio_minutes} min</p>
                <p className="text-xs text-gray-500">Estimated audio generated</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 bg-white/5 rounded-lg text-center">
                <p className="text-xl font-bold font-mono">{stats.with_tts_audio}</p>
                <p className="text-[10px] text-gray-500 uppercase">Episodes with TTS</p>
              </div>
              <div className="p-3 bg-white/5 rounded-lg text-center">
                <p className="text-xl font-bold font-mono">{stats.voice_profiles}</p>
                <p className="text-[10px] text-gray-500 uppercase">Voice Profiles</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 bg-white/5 rounded-lg">
                <p className="text-[10px] text-gray-500 uppercase mb-1">Last Generated</p>
                <p className="text-sm font-mono" data-testid="last-generated">{formatTimeAgo(stats.last_generated_at)}</p>
              </div>
              <div className="p-3 bg-white/5 rounded-lg">
                <p className="text-[10px] text-gray-500 uppercase mb-1">Est. Time to Complete</p>
                <p className="text-sm font-mono" data-testid="est-remaining">
                  {stats.remaining_episodes > 0
                    ? `~${Math.round(stats.est_time_remaining_min / 60)}h ${Math.round(stats.est_time_remaining_min % 60)}m`
                    : "Done!"}
                </p>
              </div>
            </div>
            <div className="p-3 bg-white/5 rounded-lg">
              <p className="text-[10px] text-gray-500 uppercase mb-1">Total Audio Size</p>
              <p className="text-sm font-mono">{(stats.total_audio_bytes / (1024 * 1024)).toFixed(1)} MB</p>
            </div>
          </div>
        </div>
      </div>

      {/* TTS Coverage by Season */}
      <div className="p-6 border border-white/10 rounded-xl bg-white/[0.02]" data-testid="tts-by-season">
        <h3 className="font-anton text-sm tracking-wider mb-4 flex items-center gap-2">
          <BarChart3 size={16} className="text-amber-500" /> TTS COVERAGE BY SEASON
        </h3>
        <div className="space-y-3">
          {Object.entries(tts_by_season).map(([season, data]) => {
            const pct = data.total > 0 ? Math.round((data.with_tts / data.total) * 100) : 0;
            return (
              <div key={season} className="flex items-center gap-3" data-testid={`tts-season-${season}`}>
                <span className="text-xs font-mono text-gray-400 w-8">S{season}</span>
                <div className="flex-1 h-6 bg-white/5 rounded-lg overflow-hidden relative">
                  <div className="h-full bg-gradient-to-r from-blue-600 to-cyan-500 rounded-lg transition-all duration-700" style={{ width: `${pct}%` }} />
                  <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold font-mono">
                    {data.with_tts}/{data.total} ({pct}%)
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Status Breakdown + Priority Queue */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Status Breakdown */}
        <div className="p-6 border border-white/10 rounded-xl bg-white/[0.02]" data-testid="status-breakdown">
          <h3 className="font-anton text-sm tracking-wider mb-4">STATUS BREAKDOWN</h3>
          <div className="space-y-2">
            {Object.entries(STATUS_COLORS).map(([status, bg]) => {
              const count = status_breakdown[status] || 0;
              const pct = stats.total_episodes > 0 ? Math.round((count / stats.total_episodes) * 100) : 0;
              return (
                <div key={status} className="flex items-center gap-3">
                  <span className={`w-2 h-2 rounded-full ${bg}`} />
                  <span className="text-xs capitalize text-gray-400 w-20">{status}</span>
                  <div className="flex-1 h-3 bg-white/5 rounded-full overflow-hidden">
                    <div className={`h-full ${bg} rounded-full transition-all`} style={{ width: `${pct}%` }} />
                  </div>
                  <span className="text-xs font-mono text-gray-500 w-16 text-right">{count} ({pct}%)</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Priority Queue */}
        <div className="p-6 border border-white/10 rounded-xl bg-white/[0.02]" data-testid="priority-queue">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-anton text-sm tracking-wider">NEXT TO GENERATE (TTS PRIORITY)</h3>
            <div className="flex gap-2">
              <button onClick={fetchStats} className="p-1.5 rounded hover:bg-white/10 transition-colors" data-testid="refresh-dashboard-btn">
                <RefreshCw size={14} className="text-gray-500" />
              </button>
            </div>
          </div>

          {/* Generate Next N button */}
          {priority_queue.length > 0 && (
            <div className="mb-4">
              {!generating ? (
                <div className="flex gap-2">
                  <button onClick={() => generateNext(5)} disabled={generating}
                    className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white text-sm font-bold rounded-lg transition-all shadow-lg shadow-blue-600/20"
                    data-testid="generate-next-5-btn">
                    <Zap size={14} /> Generate Next 5
                  </button>
                  <button onClick={() => generateNext(1)} disabled={generating}
                    className="flex items-center gap-2 px-3 py-2.5 bg-white/5 hover:bg-white/10 text-gray-300 text-sm rounded-lg transition-all"
                    data-testid="generate-next-1-btn">
                    <Zap size={14} /> Just 1
                  </button>
                </div>
              ) : (
                <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg" data-testid="gen-progress">
                  <div className="flex items-center gap-3 mb-2">
                    <Loader2 size={16} className="animate-spin text-blue-400" />
                    <span className="text-sm text-blue-300">Generating {genProgress.done + 1}/{genProgress.total}...</span>
                  </div>
                  <p className="text-xs text-gray-400 truncate">{genProgress.current}</p>
                  <div className="h-1.5 bg-white/10 rounded-full overflow-hidden mt-2">
                    <div className="h-full bg-blue-500 rounded-full transition-all" style={{ width: `${genProgress.total > 0 ? (genProgress.done / genProgress.total) * 100 : 0}%` }} />
                  </div>
                </div>
              )}
            </div>
          )}

          {priority_queue.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-4">All episodes with stories have TTS audio!</p>
          ) : (
            <div className="space-y-1 max-h-[250px] overflow-y-auto">
              {priority_queue.map((ep, i) => (
                <button key={ep.id} onClick={() => navigate(`/episodes/${ep.id}`)}
                  className="w-full flex items-center gap-3 px-3 py-2 rounded hover:bg-white/5 transition-colors text-left group"
                  data-testid={`priority-ep-${ep.id}`}>
                  <span className="text-[10px] font-mono text-gray-600 w-5">{i + 1}</span>
                  <span className="text-[10px] font-mono text-gray-500 w-12">S{ep.season}E{ep.episode_number}</span>
                  <span className="flex-1 text-sm truncate">{ep.title}</span>
                  <ChevronRight size={14} className="text-gray-600 group-hover:text-red-500 transition-colors" />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductionDashboard;
